package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.LFieldErrorStyle
import dev.lingvo.site.ds.LFieldLabelStyle
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LRouteTransition
import dev.lingvo.site.ds.LTextFieldFrameStyle
import dev.lingvo.site.ds.LTextFieldInputStyle
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.LWordmarkGlyph
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.WORDMARK_ROTATE_HANDOFF_MS
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.random.Random
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.iconSize

/**
 * The landing page's argument, at a fifth of the size and holding still.
 *
 * `ScriptWall` sets these same nine greetings at 88px and lets three lines settle in on load; that
 * is the marketing page's one self-starting moment and it earns it, because a visitor arriving
 * there is looking at nothing else. This screen is looked at by somebody typing a password, so the
 * echo keeps the content and drops the movement entirely: no timer, no carousel, no entrance.
 *
 * The greetings carry their locale code because that is the pair the product actually deals in — a
 * string and the locale it belongs to — and because nine words alone would be the wall shrunk
 * rather than the wall restated. `en` takes the accent for the same reason `Hello` does on the
 * wall: one marked member turns a swatch into a sentence.
 *
 * `lang` is load-bearing, not decorative. Han is unified in Unicode, so the face that draws 你好 is
 * chosen by this attribute and nothing else. The codes themselves are deliberately untagged — they
 * are Latin text *naming* a language, not text *in* it, and `lang="ru"` on the string "ru" tells a
 * screen reader to pronounce two Latin letters as Russian.
 */
private data class AuthGreeting(val text: String, val code: String, val accent: Boolean = false)

/**
 * The wall's nine, in the wall's order, so the two screens are recognisably the same list.
 *
 * **Deliberately not translatable**, for the reason `ScriptWall` gives about its own specimens: each
 * word is paired with a `lang` that selects the face which draws it, and a translator editing
 * "Привет" into another language would leave `lang="ru"` behind it and silently break the font
 * selection this list exists to demonstrate. These are samples of writing systems, not copy.
 *
 * They had been wrapped in `tr(...)` regardless, which was doubly wrong: this is a top-level `val`
 * evaluated at class-init, before any catalogue is fetched, so every lookup returned its own English
 * fallback anyway. The band's accessible name is the translatable string here.
 */
private val AUTH_GREETINGS = listOf(
    AuthGreeting("Hello", "en", accent = true),
    AuthGreeting("你好", "zh"),
    AuthGreeting("مرحبا", "ar"),
    AuthGreeting("Привет", "ru"),
    AuthGreeting("こんにちは", "ja"),
    AuthGreeting("Olá", "pt"),
    AuthGreeting("नमस्ते", "hi"),
    AuthGreeting("안녕", "ko"),
    AuthGreeting("Γεια", "el"),
)

/**
 * The one standing line on this screen, and it no longer rotates.
 *
 * Three of these used to take turns on a 5.2 second timer, with a progress bar and a pause button
 * to apologise for it. A panel that changes under a reader who is trying to remember a password is
 * not atmosphere, it is a distraction with a control to switch it off — and the controls were the
 * only interactive thing on the screen that was not part of signing in.
 */
private const val AUTH_NOTE = "A product keeps its voice when every locale gets the full sentence."

val AuthPageStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(colorMode.toSitePalette().surfaceBase)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
            }
    }
    // LG, not MD. The aside's 380px floor is a measure — the width the greeting wall needs to read
    // as a wall — and it does not yield. At 768px it took 380 of 768, leaving the form column 388px
    // and, once this column's own MD padding is removed, a 324px content box: *narrower* than the
    // 343px the same form gets on a 375px phone. The form shrank as the screen grew, which is the
    // one thing a responsive split must never do. At 1024px the aside's share clears its own floor
    // (491px) and the form column keeps 533px, so the ladder climbs the whole way up.
    Breakpoint.LG {
        Modifier.styleModifier {
            property("grid-template-columns", "minmax(0, 1.04fr) minmax(380px, .96fr)")
        }
    }
}

/**
 * The form side, on the marketing page's own paper.
 *
 * The padding climbs 16 -> 32 -> 48 as the band ladder climbs 24 -> 72 -> 92: not the same numbers,
 * because this column is a form rather than a full-bleed band, but the same shape of decision, so
 * arriving here from the landing page does not feel like arriving at a different site's sheet.
 */
val AuthFormSideStyle = CssStyle {
    base {
        Modifier
            .minHeight(620.px)
            .padding(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-rows", "auto 1fr")
            }
    }
    Breakpoint.MD {
        Modifier
            .minHeight(100.vh)
            .padding(Space.XXXL)
    }
    Breakpoint.LG {
        Modifier.padding(Space.Huge)
    }
}

val AuthFormContentStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(28.cssRem)
        .styleModifier {
            property("align-self", "center")
            property("justify-self", "center")
        }
}

/**
 * The title, set exactly as every band heading on the landing page is set.
 *
 * It was `TypeScale.Display` at 700 with default tracking and no responsive step, which is the one
 * thing on this screen that could be spotted as not belonging: Bricolage Grotesque sets loose above
 * 44px, so every other display-size heading in this codebase carries -1.5px and this one did not.
 * Dropping to [TypeScale.Heading] below MD is `BandHeadingStyle`'s rule too — 44px of display face
 * across a phone is four words a line.
 */
val AuthTitleStyle = CssStyle {
    base {
        Modifier
            .displayFont()
            .fontSize(TypeScale.Display)
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .letterSpacing((-1.5).px)
            .margin(0.px)
            .color(colorMode.toSitePalette().textPrimary)
    }
    Breakpoint.ZERO { Modifier.fontSize(TypeScale.Heading) }
    Breakpoint.MD { Modifier.fontSize(TypeScale.Display) }
}

val AuthSubtitleStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Body)
        .lineHeight(TypeScale.BodyLineHeight)
        .margin(0.px)
        .maxWidth(30.cssRem)
        .color(colorMode.toSitePalette().textSecondary)
}

/**
 * The pane beside the form: sunken, so it reads as let into the same sheet.
 *
 * It was [dev.lingvo.site.ds.SitePalette.surface], which on paper is *lighter* than the page — the
 * pane floated above a form it is supposed to sit behind. Sunken is the landing page's own second
 * ground (`BandGround.PaperSunken`, and the locale strip), and it is the right choice in both modes
 * rather than only in one: the inverted ink treatment would put a full-height paper slab beside a
 * password field at night.
 *
 * The hairline is a divider between two panes and is drawn in `border`, the token documented for
 * exactly that. It is not, and must never become, a coloured edge bar. It moves with the layout:
 * the panes sit side by side at MD and stacked below it, and the seam has to be on the edge they
 * actually meet at — 1.06:1 of ground separation in Night is a real step and not a visible one.
 */
val AuthArtworkStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minHeight(320.px)
            .padding(Space.XL)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    }
    Breakpoint.MD {
        Modifier
            .minHeight(100.vh)
            .padding(Space.XXXL)
            .borderLeft(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .styleModifier { property("border-top", "0") }
    }
    Breakpoint.LG {
        Modifier.padding(Space.Huge)
    }
}

val AuthArtworkInnerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(22.cssRem)
        .gap(Space.XL)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

/**
 * The eyebrow, in the landing page's label voice: mono, micro, tracked, accent, uppercased in CSS.
 *
 * Written in sentence case and transformed, because a screen reader handed "HELLO, IN NINE
 * LANGUAGES" may spell it out letter by letter. Measured 5.67:1 on paper-sunken and 6.06:1 on
 * ink-sunken, so it clears the 4.5 text floor in both modes.
 */
val AuthArtworkLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.toSitePalette().accent)
        .styleModifier { property("text-transform", "uppercase") }
}

val AuthGreetingListStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .margin(0.px)
        .padding(0.px)
        .gap(Space.MD)
        .styleModifier {
            property("list-style", "none")
            property("display", "flex")
            property("flex-direction", "column")
        }
}

/**
 * Greeting left, code right, locked to a shared baseline.
 *
 * The same row as the price card's four facts, for the same reason: a 10.5px mono code bottom- or
 * centre-aligned against a 21px word stops reading as a pair. `baseline` puts them on one line.
 */
val AuthGreetingRowStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.MD)
        .styleModifier {
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "baseline")
        }
}

/**
 * One greeting.
 *
 * [TypeScale.Reading] is the size the product reads two languages at, and it is here for the reason
 * it exists: 18px is already at the small end for CJK, where the discriminating detail fills the em
 * box. 600 rather than the wall's 700 — at a fifth of the size the heavier weight stops being
 * presence and starts being noise beside a form.
 */
val AuthGreetingWordStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Reading)
        .fontWeight(600)
        .lineHeight(TypeScale.DisplayLineHeight)
        .color(colorMode.toSitePalette().textPrimary)
}

/** The wall marks one word in the accent. So does this. */
val AuthGreetingAccentStyle = CssStyle.base {
    Modifier.color(colorMode.toSitePalette().accent)
}

/** The code, in the locale strip's resting appearance. 4.73:1 on paper-sunken, 6.52:1 on ink. */
val AuthGreetingCodeStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.toSitePalette().textMuted)
}

val AuthArtworkNoteStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textSecondary)
}

/**
 * What the auth form's fields do that the shared field does not, and nothing else.
 *
 * Three rules, each carrying an attribute selector so it outweighs `LTextFieldFrameStyle` on
 * specificity rather than on the order two `CssStyle` blocks happen to be registered in. Both
 * attributes are written unconditionally by [AuthField], so the selectors always match something.
 *
 *  - **Hover lifts the ground**, sunken to surface. A field is the only thing on this screen a
 *    visitor is meant to put a cursor in, and the shared field answers a hover with nothing at all.
 *    Ground rather than border, so it does not preview the focus state.
 *  - **Focus lifts it too**, under the accent ring the shared style already draws. The lit field is
 *    then the brightest thing in the column, which is where the caret is.
 *  - **An invalid field keeps its own colour when focused.** The shared style draws a danger border
 *    and then an accent focus ring around it, so the field that is wrong is ringed in the colour
 *    that means "act on this" — two signals disagreeing on one control. Danger 5.20:1 on paper and
 *    6.74:1 on ink, both past the 3:1 floor for a non-text boundary.
 */
val AuthFieldFrameStyle = CssStyle {
    base { Modifier }
    cssRule("[data-enabled=\"true\"]:hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surface)
    }
    cssRule("[data-invalid=\"false\"]:focus-within") {
        Modifier.backgroundColor(colorMode.toSitePalette().surface)
    }
    cssRule("[data-invalid=\"true\"]:focus-within") {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().danger)
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().danger)
    }
}

val AuthFieldHelpStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
}

/**
 * The error line, with something other than colour to tell it from the help line above it.
 *
 * The two were the same size, the same weight and the same position, separated only by hue — so a
 * reader who cannot separate red from grey met two identical lines under a field and no way to know
 * which one was the complaint. The glyph is the differentiator; the colour stays as the second cue.
 */
val AuthFieldErrorStyle = CssStyle.base {
    Modifier
        .gap(Space.XS)
        .styleModifier {
            property("display", "flex")
            property("align-items", "flex-start")
        }
}

/**
 * Holds the glyph on the first line's centre, whatever the message does.
 *
 * One line box tall, expressed as [TypeScale.BodyLineHeight] in `em` so it is the line height this
 * row is actually set in rather than a pixel value that stops being right when the step changes.
 * `align-items: center` inside it does the optical alignment; a message that wraps to two lines
 * leaves the glyph beside the first, which is where the eye starts.
 */
val AuthFieldErrorIconStyle = CssStyle.base {
    Modifier.styleModifier {
        property("display", "inline-flex")
        property("align-items", "center")
        property("flex", "0 0 auto")
        property("height", "${TypeScale.BodyLineHeight}em")
    }
}

@Composable
fun AuthPageScaffold(
    title: String,
    subtitle: String,
    alternatePrompt: String,
    alternateAction: String,
    alternatePath: String,
    content: @Composable () -> Unit,
) {
    Div(AuthPageStyle.toModifier().toAttrs()) {
        // The same enter every route in the app uses. Arriving here from the marketing page's
        // "Log in" was the one navigation in the product with no transition at all — the page
        // simply replaced itself, which reads as a reload rather than as a step. Keyed on the
        // title so moving between Log in and Sign up re-runs it; the artwork beside it does not
        // move, because only the form changed.
        LRouteTransition(routeKey = title) {
            Main(AuthFormSideStyle.toModifier().toAttrs()) {
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    PublicWordmark()
                    Spacer()
                    PublicThemeToggle()
                }
                Column(AuthFormContentStyle.toModifier().gap(Space.XXL)) {
                    // 12 rather than 8 between a 44px display title and a 16.5px lede. At the old
                    // gap the two were one block and the title stopped being a title.
                    Column(Modifier.gap(Space.MD)) {
                        H1(AuthTitleStyle.toModifier().toAttrs()) { Text(title) }
                        P(AuthSubtitleStyle.toModifier().toAttrs()) { Text(subtitle) }
                    }
                    content()
                    // "New to Lingvo?" and "Create an account" read as one sentence, so they have to
                    // sit on one line at one size. The link inherited the base font size while the
                    // prompt was BodySmall, and the row aligned to the top, which left the two visibly
                    // out of step.
                    Row(
                        Modifier.gap(Space.XS).styleModifier { property("flex-wrap", "wrap") },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Span(
                            Modifier
                                .color(ColorMode.current.toSitePalette().textSecondary)
                                .fontSize(TypeScale.BodySmall)
                                .lineHeight(TypeScale.BodyLineHeight)
                                .toAttrs(),
                        ) {
                            Text(alternatePrompt)
                        }
                        PublicTextLink(
                            alternatePath,
                            alternateAction,
                            Modifier
                                .fontSize(TypeScale.BodySmall)
                                .lineHeight(TypeScale.BodyLineHeight)
                                .fontWeight(600),
                        )
                    }
                }
            }
        }
        AuthArtwork()
    }
}

@Composable
fun AuthField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholderText: String,
    inputType: InputType<String>,
    name: String,
    autocomplete: String,
    error: String?,
    helperText: String? = null,
    enabled: Boolean,
    onBlur: () -> Unit,
    onCommit: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val inputId = remember { "lingvo-auth-${Random.nextInt(1_000_000)}" }
    val errorId = "$inputId-error"
    val helperId = "$inputId-help"
    val describedBy = listOfNotNull(
        helperId.takeIf { helperText != null },
        errorId.takeIf { error != null },
    ).joinToString(" ")
    // Two steps, not one. The label belongs to its field and sits 4px above it; the help and the
    // error are commentary on the pair and sit 8px below. At the old flat 4px the focus ring —
    // 2px wide at a 2px offset, so 4px clear of the frame — landed on top of the help text every
    // time somebody tabbed into the field.
    Column(modifier.fillMaxWidth().gap(Space.SM)) {
        Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
                Text(label)
            }
            Row(
                LTextFieldFrameStyle.toModifier()
                    .then(AuthFieldFrameStyle.toModifier())
                    // Inline, so it beats both classes rather than racing them: the shared field
                    // transitions border and shadow only, and the ground this style moves would
                    // otherwise snap. 120ms is the token for an in-place colour change.
                    .transitionColors()
                    .styleModifier { property("opacity", if (enabled) "1" else "0.56") }
                    .attr("data-invalid", (error != null).toString())
                    // Read by the hover rule. A disabled field must not answer a cursor at all —
                    // `:hover` fires on the frame whatever the input inside it is doing.
                    .attr("data-enabled", enabled.toString()),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Input(
                    type = inputType,
                    attrs = LTextFieldInputStyle.toModifier().toAttrs {
                        id(inputId)
                        value(value)
                        placeholder(placeholderText)
                        attr("name", name)
                        attr("autocomplete", autocomplete)
                        attr("autocapitalize", if (name == "name") "words" else "none")
                        attr("spellcheck", if (name == "name") "true" else "false")
                        attr("aria-required", "true")
                        if (!enabled) disabled()
                        if (describedBy.isNotBlank()) attr("aria-describedby", describedBy)
                        if (error != null) {
                            attr("aria-invalid", "true")
                        }
                        onInput { onValueChange(it.value) }
                        onBlur { onBlur() }
                        onKeyDown { event ->
                            if (event.key == "Enter" && !event.isComposing) {
                                event.preventDefault()
                                onCommit()
                            }
                        }
                    },
                )
            }
        }
        if (helperText != null) {
            Div(AuthFieldHelpStyle.toModifier().toAttrs { id(helperId) }) {
                Text(helperText)
            }
        }
        if (error != null) {
            Div(
                LFieldErrorStyle.toModifier()
                    .then(AuthFieldErrorStyle.toModifier())
                    .toAttrs {
                        id(errorId)
                        attr("role", "alert")
                    },
            ) {
                // Inherits `danger` from the error style, so the glyph and the sentence are one
                // object rather than an icon that has to be coloured separately and drifts.
                Span(AuthFieldErrorIconStyle.toModifier().toAttrs()) {
                    LWarningIcon(Modifier.iconSize(IconSize.MD))
                }
                Span { Text(error) }
            }
        }
    }
}

/**
 * The pane beside the form. Nothing in it moves, and nothing in it is on a timer.
 *
 * `GlyphMosaic` used to sit behind this at 20% opacity, which meant these same nine greetings
 * appeared twice — once as texture and once as content — competing with themselves. The mosaic is
 * for surfaces with nothing else to say; this one now says it plainly.
 *
 * It comes after the form in the DOM, so a screen reader reaches the fields first and meets the
 * greetings only on the way out. Each is tagged with its language, which is the one thing on this
 * screen that makes the pane worth reading aloud at all: nine greetings pronounced correctly is the
 * product's whole subject.
 */
@Composable
private fun AuthArtwork() {
    Aside(AuthArtworkStyle.toModifier().toAttrs()) {
        Div(AuthArtworkInnerStyle.toModifier().toAttrs()) {
            Div(AuthArtworkLabelStyle.toModifier().toAttrs()) { Text(t("marketing.auth_scaffold.hello_in_nine_languages", "Hello, in nine languages")) }
            Ul(AuthGreetingListStyle.toModifier().toAttrs()) {
                AUTH_GREETINGS.forEach { greeting ->
                    Li(AuthGreetingRowStyle.toModifier().toAttrs()) {
                        Span(
                            AuthGreetingWordStyle.toModifier()
                                .then(
                                    if (greeting.accent) {
                                        AuthGreetingAccentStyle.toModifier()
                                    } else {
                                        Modifier
                                    },
                                )
                                .toAttrs { attr("lang", greeting.code) },
                        ) { Text(greeting.text) }
                        Span(AuthGreetingCodeStyle.toModifier().toAttrs()) { Text(greeting.code) }
                    }
                }
            }
            P(AuthArtworkNoteStyle.toModifier().toAttrs()) { Text(AUTH_NOTE) }
        }
    }
}

/**
 * The screen between "Start free" and the app, for a visitor who already has a session.
 *
 * **The bug this closes is a flash of the wrong page.** `Session.state` begins as `Loading`
 * whenever a token is in storage, and both auth pages painted their form unconditionally and only
 * redirected from a `LaunchedEffect` that runs *after* composition. So a returning customer
 * clicking "Start free" got: the sign-up form, held for the whole round trip of `/auth/me`, then a
 * jump to `/app`. On a cold load that is one to two seconds of being asked to create an account
 * they already have — the product's own answer to its own question, shown backwards.
 *
 * The fix is to decide before painting, and to draw the decision while it is being made.
 *
 * **Why this surface and not the app's skeleton.** `AppShellLoading` is the right thing once the
 * destination is known; it is the wrong thing while it is not, because a token can still turn out
 * to be expired and drawing a workspace around it promises a place the visitor may not be going.
 * This draws the one thing that is true at that moment: Lingvo, deciding.
 *
 * **The mark is the progress cue.** DESIGN.md specifies the logo as "a bracket enclosing a rotating
 * glyph", so the rotation here is the mark doing what it is already defined to do rather than a
 * spinner borrowed from somewhere else. It also degrades correctly on its own: under
 * `prefers-reduced-motion` the glyph holds at `[あ]` and the line of copy carries the whole message,
 * which is why the copy is a sentence and not the word "Loading".
 */
@Composable
fun AuthHandoff(
    message: String,
    onRetry: (() -> Unit)? = null,
    onUseAnotherAccount: (() -> Unit)? = null,
) {
    Div(AuthHandoffStyle.toModifier().toAttrs()) {
        Column(
            AuthHandoffInnerStyle.toModifier(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Row(
                Modifier.gap(Space.SM),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(AuthHandoffMarkStyle.toModifier().toAttrs { attr("data-auth-wordmark-mark", "") }) {
                    LWordmarkGlyph(
                        Modifier
                            .monoFont()
                            .fontSize(TypeScale.Heading)
                            .fontWeight(600)
                            .lineHeight(1)
                            .color(ColorMode.current.toSitePalette().accent),
                        intervalMs = WORDMARK_ROTATE_HANDOFF_MS,
                    )
                }
                Span(
                    Modifier
                        .displayFont()
                        .fontSize(TypeScale.Heading)
                        .fontWeight(700)
                        .color(ColorMode.current.toSitePalette().textPrimary)
                        .toAttrs { attr("data-auth-wordmark-label", "") },
                ) { Text(t("marketing.auth_scaffold.lingvo", "Lingvo")) }
            }
            // Polite, not assertive: this is the expected state of the page, not an interruption,
            // and an assertive region would talk over a screen reader mid-sentence on every load.
            P(
                AuthHandoffMessageStyle.toModifier().toAttrs {
                    attr("role", if (onRetry == null) "status" else "alert")
                    attr("aria-live", if (onRetry == null) "polite" else "assertive")
                },
            ) { Text(message) }
            if (onRetry != null && onUseAnotherAccount != null) {
                P(AuthHandoffMessageStyle.toModifier().toAttrs()) {
                    Text(t(
                        "marketing.auth_scaffold.saved_sign_in_remains",
                        "Your saved sign-in is still here. Using another account will sign it out.",
                    ))
                }
                Row(Modifier.gap(Space.SM).styleModifier {
                    property("flex-wrap", "wrap")
                    property("justify-content", "center")
                }) {
                    LButton(
                        label = t("marketing.auth_scaffold.try_again", "Retry session check"),
                        onClick = onRetry,
                    )
                    LButton(
                        label = t("marketing.auth_scaffold.use_another_account", "Use another account"),
                        onClick = onUseAnotherAccount,
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            }
        }
    }
}

val AuthHandoffMarkStyle = CssStyle.base {
    Modifier
        .width(80.px)
        .height(40.px)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("flex-shrink", "0")
        }
}

/**
 * The same ground as the marketing page and as `/app`.
 *
 * DESIGN.md fixes `surfaceBase` as "identical to marketing; the `/app` seam has no colour change",
 * and this screen sits exactly on that seam. Giving it a tint of its own would invent a third place
 * in a journey that has two.
 */
val AuthHandoffStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minHeight(100.vh)
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .styleModifier {
            property("display", "grid")
            property("place-items", "center")
        }
}

val AuthHandoffInnerStyle = CssStyle.base {
    Modifier
        .padding(Space.XXXL)
        .gap(Space.LG)
        .maxWidth(420.px)
        .styleModifier { property("text-align", "center") }
}

val AuthHandoffMessageStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textSecondary)
}
